import { Component } from "@lisergia/core";

interface RedirectEntry {
  grade: string;
  subject: string;
  theme: string;
  oldUrl: string;
}

export default class extends Component {
  declare classes: {
    active: string;
    optionActive: string;
    itemActive: string;
    itemDisabled: string;
  };

  declare element: HTMLElement;
  declare elements: {
    title: HTMLElement;
    options: NodeListOf<HTMLElement>;
    descriptions: NodeListOf<HTMLElement>;
    lists: NodeListOf<HTMLElement>;
    items: NodeListOf<HTMLElement>;
    button: HTMLElement;
  };

  redirects: RedirectEntry[] = [];

  constructor({ element }: { element: HTMLElement }) {
    super({
      classes: {
        active: "search--active",
        optionActive: "search__option--active",
        itemActive: "search__option__list__item--active",
        itemDisabled: "search__option__list__item--disabled",
      },
      element,
      elements: {
        title: ".search__form__title",
        options: ".search__option",
        descriptions: ".search__option__description",
        lists: ".search__option__list",
        items: ".search__option__list__item",
        button: ".search__button",
      },
    });

    this.initSelections();
    this.fetchRedirects();
    this.addEventListeners();
  }

  selections: Map<number, { label: string; value: string }> = new Map();

  initSelections() {
    this.elements.options.forEach((option, index) => {
      const activeItem = option.querySelector(
        `.${this.classes.itemActive}`,
      ) as HTMLButtonElement | null;

      if (activeItem) {
        const value = activeItem.value;
        const label = activeItem.textContent?.trim() || "";

        this.selections.set(index, { label, value });
      }
    });
  }

  async fetchRedirects() {
    const csvUrl = this.element.dataset.search;

    if (!csvUrl) return;

    try {
      const response = await fetch(csvUrl);
      const text = await response.text();

      this.redirects = text
        .split("\n")
        .filter((line) => line.trim())
        .map((line) => {
          const [grade, subject, theme, oldUrl] = line
            .split(",")
            .map((field) => field.trim());

          return {
            grade: grade.toLowerCase(),
            subject: subject.toLowerCase(),
            theme: theme.toLowerCase(),
            oldUrl,
          };
        });

      this.updateAvailability();
    } catch (error) {
      console.error("Failed to fetch search redirects:", error);
    }
  }

  /**
   * For each item in every dropdown, check if there's at least one CSV row
   * with a valid (non-N/A) oldUrl that matches:
   * - The item's value for its own field (exact match)
   * - The current selection for the other fields (exact match, or skip if unselected)
   */
  updateAvailability() {
    let changed = false;

    const grade = this.selections.get(0)?.value?.toLowerCase();
    const subject = this.selections.get(1)?.value?.toLowerCase();
    const theme = this.selections.get(2)?.value?.toLowerCase();

    this.elements.items.forEach((item) => {
      const btn = item as HTMLButtonElement;
      const value = btn.value.toLowerCase();
      const optionIndex = this.getOptionIndex(item);

      const hasMatch = this.redirects.some((r) => {
        if (r.oldUrl === "N/A") return false;

        const gradeMatch =
          optionIndex === 0 ? r.grade === value : !grade || r.grade === grade;
        const subjectMatch =
          optionIndex === 1
            ? r.subject === value
            : !subject || r.subject === subject;
        const themeMatch =
          optionIndex === 2 ? r.theme === value : !theme || r.theme === theme;

        return gradeMatch && subjectMatch && themeMatch;
      });

      if (hasMatch) {
        btn.classList.remove(this.classes.itemDisabled);
        btn.disabled = false;
      } else {
        btn.classList.add(this.classes.itemDisabled);
        btn.disabled = true;

        // If this disabled item was selected, deselect it
        const selected = this.selections.get(optionIndex);

        if (selected && selected.value.toLowerCase() === value) {
          this.selections.delete(optionIndex);
          btn.classList.remove(this.classes.itemActive);
          this.elements.descriptions[optionIndex].textContent =
            this.elements.descriptions[optionIndex].dataset.placeholder || "";
          changed = true;
        }
      }
    });

    // Re-run if any selections were cleared, so other dropdowns update
    if (changed) {
      this.updateAvailability();
    }
  }

  findRedirectUrl(
    grade: string,
    subject: string,
    theme: string,
  ): string | null {
    const g = grade.toLowerCase();
    const s = subject.toLowerCase();
    const t = theme.toLowerCase();

    const match = this.redirects.find(
      (r) =>
        r.oldUrl !== "N/A" && r.grade === g && r.subject === s && r.theme === t,
    );

    return match ? match.oldUrl : null;
  }

  getOptionIndex(item: HTMLElement): number {
    const option = item.closest(".search__option") as HTMLElement;

    return Array.from(this.elements.options).indexOf(option);
  }

  onItemClick(event: Event) {
    const item = event.currentTarget as HTMLButtonElement;

    if (item.disabled) return;

    const optionIndex = this.getOptionIndex(item);
    const value = item.value;
    const label = item.textContent?.trim() || "";

    const list = item.closest(".search__option__list") as HTMLElement;
    const siblings = list.querySelectorAll(".search__option__list__item");

    siblings.forEach((sibling) => {
      sibling.classList.remove(this.classes.itemActive);
    });

    const current = this.selections.get(optionIndex);

    if (current && current.value === value) {
      this.selections.delete(optionIndex);

      this.elements.descriptions[optionIndex].textContent =
        this.elements.descriptions[optionIndex].dataset.placeholder || "";
    } else {
      this.selections.set(optionIndex, { label, value });

      item.classList.add(this.classes.itemActive);

      this.elements.descriptions[optionIndex].textContent = label;
    }

    this.updateAvailability();
  }

  onTitleClick() {
    this.element.classList.toggle(this.classes.active);
  }

  onOptionClick(event: Event) {
    const option = event.currentTarget as HTMLElement;

    this.elements.options.forEach((element) => {
      if (element !== option) {
        element.classList.remove(this.classes.optionActive);
      }
    });

    option.classList.toggle(this.classes.optionActive);
  }

  onButtonClick() {
    const grade = this.selections.get(0)?.value || "all";
    const subject = this.selections.get(1)?.value || "all";
    const theme = this.selections.get(2)?.value || "all";

    const url = this.findRedirectUrl(grade, subject, theme);

    if (url) {
      window.location.href = url;
    }
  }

  addEventListeners() {
    this.elements.title.addEventListener("click", this.onTitleClick);

    this.elements.options.forEach((option) => {
      option.addEventListener("click", this.onOptionClick);
    });

    this.elements.items.forEach((item) => {
      item.addEventListener("click", this.onItemClick);
    });

    this.elements.button.addEventListener("click", this.onButtonClick);
  }

  removeEventListeners() {
    this.elements.title.removeEventListener("click", this.onTitleClick);

    this.elements.options.forEach((option) => {
      option.removeEventListener("click", this.onOptionClick);
    });

    this.elements.items.forEach((item) => {
      item.removeEventListener("click", this.onItemClick);
    });

    this.elements.button.removeEventListener("click", this.onButtonClick);
  }
}
